import useOnboardingField from "@features/onboarding/hooks/useOnboardingField";
import { ONBOARDING_FIELDS } from "@fitties/shared";
import clsx from "clsx";
import { ChevronLeft } from "lucide-react";

interface OnboardingAppBarProps {
  /** Optional CSS class name */
  className?: string;
  /** Whether the user can navigate back */
  canGoBack?: boolean;
  /** Callback fired when the back button is pressed */
  onBack?: () => void;
  /** Override for which step index to highlight up to */
  activeStepIndex?: number;
}

/**
 * App bar component for the onboarding flow.
 *
 * Displays a progress indicator and an optional back button
 * for navigating to a previous onboarding step.
 */
export default function OnboardingAppBar({ className, canGoBack, onBack, activeStepIndex }: OnboardingAppBarProps) {
  const { currentFieldIndex } = useOnboardingField();
  const highlightIndex = activeStepIndex ?? currentFieldIndex;

  return (
    <div
      data-testid="onboarding-app-bar"
      className={clsx("relative flex items-center justify-center px-4 pt-4", className)}
    >
      {canGoBack && (
        <button
          type="button"
          data-testid="onboarding-back-button"
          onClick={onBack}
          className="absolute left-4 p-1 text-black/70 hover:text-black"
        >
          <ChevronLeft className="size-6" />
        </button>
      )}

      <div className="flex items-center w-3/4 space-x-3">
        {ONBOARDING_FIELDS.map((field, index) => (
          <div
            data-current-field={field}
            data-testid="onboarding-progress-segment"
            key={field}
            className={`h-2 rounded-full transition-all duration-300 flex-1 ${
              index <= highlightIndex ? "bg-pink-500 shadow-pink-200" : "bg-white"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
